// Unified skills — types + React Query hooks (DEV-16962).
//
// Wraps the unified /v1/skills API (DEV-16810) that replaces theopolis's
// skills/* routes — see lib/theopolis.ts ISkill/ICreateSkillBody/etc, now
// dead code once every caller has migrated (DEV-16820 retires it).
//
//   GET    /v1/skills                       — list (with promoted version + bound agent ids)
//   GET    /v1/skills/:id                   — detail
//   GET    /v1/skills/:id/versions          — version history (newest first)
//   POST   /v1/skills                       — create (creates + promotes version 1)
//   PATCH  /v1/skills/:id                   — content edit (cuts a new version; comment required)
//   PATCH  /v1/skills/:id/bindings          — binding edit (never cuts a version)
//   POST   /v1/skills/:id/promote-version   — rollback to an existing version (DEV-16963)
//   DELETE /v1/skills/:id                   — soft-delete
//
// Model differences from theopolis, by design (DEV-16805 unified store):
//   - description/body are NOT flat fields on the skill row — they live on
//     `currentVersion` (nested). The parent row is identity + binding + a
//     pointer.
//   - a skill MUST bind to at least one agent or set `appliesToAll` — there
//     is no implicit "applies to everything" default the way theopolis's
//     flat org_skill table had.
//   - content edits and binding edits are separate endpoints (PATCH :id vs
//     PATCH :id/bindings) — renaming the skill's binding set never cuts a
//     version; editing description/body always does (comment required).
//   - SkillVersion has no server-computed `isCurrent` flag; compare
//     `version.id === skill.currentVersionId` client-side.

import { useClient } from '@cortex/react-query';
import type {
  CreateSkillInput,
  PromoteSkillVersionInput,
  SkillVersion,
  SkillWithBindings,
  UpdateSkillBindingsInput,
  UpdateSkillVersionInput,
} from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type {
  CreateSkillInput,
  PromoteSkillVersionInput,
  SkillVersion,
  SkillWithBindings,
  UpdateSkillBindingsInput,
  UpdateSkillVersionInput,
};

interface IPaginatedEnvelope<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

const skillListKey = ['skills'] as const;
// Collection list rows carry a skillCount — skill writes that (un)bind
// collections must refresh them too (lib/agent-collection.ts owns the key).
const collectionListKey = ['agent-collections'] as const;

// Agent-side skills only. The unified /v1/skills table also holds doc-processing
// skills (kind ∈ conversion|passthrough|custom-transform, e.g. the built-in
// pdf-to-markdown / *-passthrough converters). Every consumer of this hook — the
// Agents ▸ Skills catalog and the workflow node→skill binding picker — is an
// agent-binding surface, so scope the query to kind=agent server-side. Without
// this, built-in doc skills leak into the Agent Skills view.
export function useSkillList() {
  const client = useClient();
  return useQuery({
    queryKey: skillListKey,
    queryFn: () =>
      client.http
        .get('v1/skills?limit=200&kind=agent')
        .json<IPaginatedEnvelope<SkillWithBindings>>()
        .then((page) => page.items),
  });
}

/**
 * The exact skills an agent RESOLVES at run time (DEV-17002) — appliesToAll ∪
 * bound-to-agent ∪ via-a-collection — from the shared resolver. Use for the
 * per-agent "what this agent sees" view.
 */
export function useResolvedSkills(agentId: string) {
  const client = useClient();
  return useQuery({
    queryKey: [...skillListKey, 'resolved', agentId] as const,
    queryFn: () =>
      client.http
        .get('v1/skills/resolved', { searchParams: { agentId } })
        .json<SkillWithBindings[]>(),
    enabled: Boolean(agentId),
  });
}

export function useSkillVersions(skillId: string | null) {
  const client = useClient();
  return useQuery({
    queryKey: [...skillListKey, skillId, 'versions'] as const,
    queryFn: () => client.http.get(`v1/skills/${skillId}/versions`).json<SkillVersion[]>(),
    enabled: Boolean(skillId),
  });
}

export function useCreateSkill() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSkillInput) =>
      client.http.post('v1/skills', { json: body }).json<SkillWithBindings>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: skillListKey });
      await qc.invalidateQueries({ queryKey: collectionListKey });
    },
  });
}

/** Content edit: cuts a new immutable version and promotes it (comment required). */
export function useUpdateSkillVersion() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateSkillVersionInput }) =>
      client.http.patch(`v1/skills/${id}`, { json: patch }).json<SkillWithBindings>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: skillListKey });
    },
  });
}

/** Binding edit (who the skill applies to) — never cuts a version. */
export function useUpdateSkillBindings() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateSkillBindingsInput }) =>
      client.http.patch(`v1/skills/${id}/bindings`, { json: patch }).json<SkillWithBindings>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: skillListKey });
      await qc.invalidateQueries({ queryKey: collectionListKey });
    },
  });
}

/** Rollback: re-point currentVersionId at an existing older version (DEV-16963). */
export function usePromoteSkillVersion(skillId: string) {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) => {
      const body: PromoteSkillVersionInput = { versionId };
      return client.http
        .post(`v1/skills/${skillId}/promote-version`, { json: body })
        .json<SkillWithBindings>();
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: skillListKey });
      await qc.invalidateQueries({ queryKey: [...skillListKey, skillId, 'versions'] });
    },
  });
}

/** Soft-delete (skills, unlike memories, may be deleted). */
export function useDeleteSkill() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.http.delete(`v1/skills/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: skillListKey });
      await qc.invalidateQueries({ queryKey: collectionListKey });
    },
  });
}
